import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FileCsv, FileXls, FilePdf, DownloadSimple, Funnel, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const FORMATS = [
  { value: "csv", label: "CSV", icon: FileCsv, desc: "Archivo de valores separados por coma" },
  { value: "excel", label: "Excel", icon: FileXls, desc: "Hoja de cálculo Microsoft Excel" },
  { value: "pdf", label: "PDF", icon: FilePdf, desc: "Documento portable (máx. 200 registros)" },
];

export default function ExportPage() {
  const [format, setFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ search: "", category: "", status: "", entity: "" });
  const [entities, setEntities] = useState([]);
  const [tenderCount, setTenderCount] = useState(0);

  useEffect(() => {
    api.get("/tenders/categories").then(r => setCategories(r.data.categories)).catch(() => {});
    api.get("/tenders/entities").then(r => setEntities(r.data.entities)).catch(() => {});
    api.get("/tenders?limit=1").then(r => setTenderCount(r.data.total)).catch(() => {});
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const cleanFilters = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) cleanFilters[k] = v; });
      const res = await api.post("/export", { format, filters: cleanFilters }, { responseType: "blob" });
      const ext = format === "excel" ? "xlsx" : format;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `licitaciones.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportación ${format.toUpperCase()} descargada`);
    } catch (err) {
      toast.error("Error al exportar. Verifica que hay datos disponibles.");
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="export-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Centro de Exportación</h1>
        <p className="text-muted-foreground text-sm mt-1">Genera reportes de licitaciones en diferentes formatos</p>
      </div>

      {/* Format selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border" data-testid="format-selector">
        {FORMATS.map((f) => {
          const Icon = f.icon;
          const isSelected = format === f.value;
          return (
            <button
              key={f.value}
              data-testid={`format-${f.value}`}
              onClick={() => setFormat(f.value)}
              className={`p-6 text-left transition-colors duration-200 ${
                isSelected ? "bg-[#002FA7] text-white" : "bg-card hover:bg-secondary"
              }`}
            >
              <Icon size={32} weight="duotone" className={isSelected ? "text-white" : "text-[#002FA7]"} />
              <p className="font-heading font-bold text-lg mt-3">{f.label}</p>
              <p className={`text-xs mt-1 ${isSelected ? "text-white/70" : "text-muted-foreground"}`}>{f.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="rounded-none shadow-none border border-border/40" data-testid="export-filters">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading font-bold text-sm flex items-center gap-2">
            <Funnel size={16} weight="duotone" className="text-[#002FA7]" />
            Filtros de Exportación (Opcional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Buscar texto</Label>
              <Input
                data-testid="export-search"
                className="rounded-none h-10 bg-card"
                placeholder="NOG, título..."
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Categoría</Label>
              <Select value={filters.category} onValueChange={v => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="export-category">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Estado</Label>
              <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="export-status">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Vigente">Vigente</SelectItem>
                  <SelectItem value="Adjudicado">Adjudicado</SelectItem>
                  <SelectItem value="Desierto">Desierto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Entidad</Label>
              <Select value={filters.entity} onValueChange={v => setFilters({ ...filters, entity: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="export-entity">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todas</SelectItem>
                  {entities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export action */}
      <Card className="rounded-none shadow-none border border-border/40">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-xs text-muted-foreground uppercase">Licitaciones disponibles</p>
              <p className="font-heading font-light text-3xl text-[#002FA7] mt-1" data-testid="export-count">{tenderCount}</p>
            </div>
            <Button
              data-testid="export-button"
              className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white h-12 px-8 font-bold"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <><Spinner size={18} className="mr-2 animate-spin" /> Exportando...</>
              ) : (
                <><DownloadSimple size={18} className="mr-2" /> EXPORTAR {format.toUpperCase()}</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
